import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Autocomplete,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';

import ordersApi from '../../../api/ordersApi';
import productsApi from '../../../api/productsApi';
import { applyServerErrors, getErrorMessage } from '../../../utils/errorHandler';
import { EXPRESS_PAYMENT_METHODS, paymentMethodLabel } from '../../../utils/paymentMethods';
import QuantityStepper from '../../../sections/client/products/QuantityStepper';
import OrderDetailDialog from '../../../sections/admin/orders/OrderDetailDialog';

const schema = yup.object({
  currency: yup.string().oneOf(['PEN', 'USD']).required(),
  payment_method: yup.string().required('Selecciona un método de pago'),
  customer_name: yup.string().trim().max(255),
});

const emptyValues = {
  currency: 'PEN',
  payment_method: 'efectivo',
  customer_name: '',
};

// Cada linea es un producto del catalogo ({ product, quantity, serials }) o un producto "libre"
// que no esta registrado ({ key, description, unit_price, quantity }), con su precio en la
// moneda de la venta.
const itemKey = (item) => (item.product ? `p-${item.product.id}` : item.key);

const formatMoney = (value, currency) =>
  new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'es-PE', { style: 'currency', currency }).format(value || 0);

// Venta express: registra en el momento una venta ya realizada en tienda o por telefono, sin
// pasar por el checkout publico. Los productos se agregan escaneando (o escribiendo) la serie,
// el nombre, el SKU o el codigo de barras; tambien admite productos "libres" no registrados.
// Se guarda como "cancelado" (pagado) de inmediato, descontando el stock, con recojo en tienda
// (ver OrderController::storeExpress), y al registrarla se abre el detalle del pedido para emitir
// la boleta/factura o la nota de venta.
export default function ExpressSalePage() {
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();

  const [productOptions, setProductOptions] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [searchingProducts, setSearchingProducts] = useState(false);
  // Unidad encontrada cuando lo buscado es exactamente una serie (ver ProductController::index).
  const [matchedSerial, setMatchedSerial] = useState(null);
  // Opcion resaltada con las flechas: si existe, Enter la elige (lo maneja el Autocomplete).
  const highlightedOption = useRef(null);
  const searchInputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [customDescription, setCustomDescription] = useState('');
  const [customPrice, setCustomPrice] = useState('');

  const [statuses, setStatuses] = useState([]);
  const [createdOrder, setCreatedOrder] = useState(null);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: emptyValues,
  });

  const currency = watch('currency');

  // Productos y cliente enviados desde el cotizador ("Registrar venta"); luego se limpia el
  // state para que recargar la pagina no los vuelva a cargar.
  useEffect(() => {
    const fromQuote = location.state;
    if (!fromQuote?.items?.length) {
      return;
    }
    setItems(
      fromQuote.items.map((item) =>
        item.product
          ? { product: item.product, quantity: item.quantity, serials: [] }
          : { key: item.key, description: item.description, unit_price: item.unit_price_pen, quantity: item.quantity }
      )
    );
    // Los productos libres del cotizador tienen su precio en soles.
    if (fromQuote.customerName || fromQuote.items.some((item) => !item.product)) {
      reset({ ...emptyValues, currency: 'PEN', customer_name: fromQuote.customerName || '' });
    }
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Estados del pedido, para el detalle que se abre al registrar la venta.
  useEffect(() => {
    ordersApi
      .getOrderStatuses()
      .then(({ data }) => setStatuses(data.data))
      .catch(() => setStatuses([]));
  }, []);

  // Primero por serie exacta y, si esa serie no existe en ningun producto, por nombre/SKU/codigo
  // de barras (serial_first, ver ProductController::index en la API).
  const searchProducts = async (search) => {
    const { data } = await productsApi.getProducts({ search, serial_first: 1, per_page: 10, is_active: 1 });
    setProductOptions(data.data);
    setMatchedSerial(data.meta?.matched_serial || null);
    return { products: data.data, serial: data.meta?.matched_serial || null };
  };

  // Busca productos conforme el usuario escribe, con un pequeno debounce.
  useEffect(() => {
    const timeout = setTimeout(async () => {
      setSearchingProducts(true);
      try {
        await searchProducts(productSearch.trim());
      } catch (error) {
        // Busqueda secundaria: si falla no se interrumpe el armado de la venta.
      } finally {
        setSearchingProducts(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSearch]);

  // `serie`: la unidad escaneada, que se vende en lugar de la mas antigua en stock.
  const addProduct = (product, serie = null) => {
    if (!product) {
      return;
    }
    if (serie && items.some((item) => item.serials?.includes(serie))) {
      enqueueSnackbar(`La serie ${serie} ya está en la venta.`, { variant: 'warning' });
    } else {
      setItems((prev) => {
        const existing = prev.find((item) => item.product?.id === product.id);
        const serials = serie ? [serie] : [];
        if (existing) {
          return prev.map((item) =>
            item.product?.id === product.id
              ? { ...item, quantity: item.quantity + 1, serials: [...(item.serials || []), ...serials] }
              : item
          );
        }
        return [...prev, { product, quantity: 1, serials }];
      });
    }
    setProductSearch('');
    setMatchedSerial(null);
  };

  // Serie encontrada para este producto que todavia se puede vender.
  const sellableSerialFor = (product, serial) =>
    serial && serial.product_id === product.id && serial.status === 'in_stock' ? serial.serie : null;

  // Enter en el buscador (la pistola lo manda al final de cada codigo): busca ya, sin esperar el
  // debounce, y agrega directo si hay un solo resultado; con varios, deja la lista para elegir.
  const handleSearchEnter = async (event) => {
    if (event.key !== 'Enter' || highlightedOption.current) {
      return;
    }
    // Sin esto, Enter en un input del formulario dispararia "Registrar venta".
    event.preventDefault();
    const code = productSearch.trim();
    if (!code) {
      return;
    }

    event.defaultMuiPrevented = true;
    setSearchingProducts(true);
    try {
      const { products, serial } = await searchProducts(code);
      if (serial && serial.status !== 'in_stock') {
        enqueueSnackbar(`La serie ${serial.serie} ya fue vendida.`, { variant: 'warning' });
      } else if (products.length === 1) {
        addProduct(products[0], sellableSerialFor(products[0], serial));
      } else if (products.length === 0) {
        enqueueSnackbar(`No se encontró ninguna serie ni producto con "${code}".`, { variant: 'warning' });
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo buscar el producto.'), { variant: 'error' });
    } finally {
      setSearchingProducts(false);
    }
  };

  const customPriceValue = Number(customPrice);
  const canAddCustom = customDescription.trim() !== '' && customPrice !== '' && customPriceValue >= 0;

  const addCustomItem = () => {
    if (!canAddCustom) {
      return;
    }
    setItems((prev) => [
      ...prev,
      { key: `c-${Date.now()}`, description: customDescription.trim(), unit_price: customPriceValue, quantity: 1 },
    ]);
    setCustomDescription('');
    setCustomPrice('');
  };

  const handleCustomKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCustomItem();
    }
  };

  // Si baja la cantidad, se descartan las ultimas series escaneadas que ya no entran.
  const updateQuantity = (key, quantity) => {
    setItems((prev) =>
      prev.map((item) =>
        itemKey(item) === key ? { ...item, quantity, serials: item.serials?.slice(0, quantity) } : item
      )
    );
  };

  const removeItem = (key) => {
    setItems((prev) => prev.filter((item) => itemKey(item) !== key));
  };

  const unitPrice = (item) => {
    if (!item.product) {
      return item.unit_price;
    }
    return currency === 'USD' ? item.product.price_usd : item.product.price_pen;
  };
  const total = items.reduce((sum, item) => sum + unitPrice(item) * item.quantity, 0);

  const resetSale = () => {
    reset(emptyValues);
    setItems([]);
    setProductSearch('');
    setMatchedSerial(null);
    setCustomDescription('');
    setCustomPrice('');
  };

  const onSubmit = async (data) => {
    if (items.length === 0) {
      enqueueSnackbar('Agrega al menos un producto a la venta.', { variant: 'warning' });
      return;
    }

    try {
      const { data: response } = await ordersApi.createExpressOrder({
        currency: data.currency,
        payment_method: data.payment_method,
        // Solo el nombre, para la nota de venta (no crea ni vincula una cuenta de cliente).
        customer_name: data.customer_name || undefined,
        items: items.map((item) =>
          item.product
            ? { product_id: item.product.id, quantity: item.quantity, serials: item.serials?.length ? item.serials : undefined }
            : { description: item.description, unit_price: unitPrice(item), quantity: item.quantity }
        ),
      });
      enqueueSnackbar(`Venta N.° ${response.data.id} registrada correctamente.`, { variant: 'success' });
      resetSale();
      // Detalle del pedido recien creado, para emitir de una vez su boleta/factura o nota de venta.
      setCreatedOrder(response.data);
    } catch (error) {
      const handled = applyServerErrors(error, setError);
      // Los errores de las lineas (p. ej. stock insuficiente, en "items") no tienen un campo del
      // formulario donde mostrarse: van como aviso.
      if (!handled || error?.response?.data?.errors?.items) {
        enqueueSnackbar(getErrorMessage(error, 'No se pudo registrar la venta.'), { variant: 'error' });
      }
    }
  };

  // Al cerrar el detalle, el foco vuelve al buscador para la siguiente venta.
  const handleDetailClose = () => {
    setCreatedOrder(null);
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };

  const refreshCreatedOrder = async () => {
    try {
      const { data } = await ordersApi.getOrder(createdOrder.id);
      setCreatedOrder(data.data);
    } catch (error) {
      // Si falla, el detalle se queda con lo ultimo que se vio.
    }
  };

  return (
    <Stack spacing={3}>
      <Stack
        sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
      >
        <div>
          <Typography variant="h4">Venta express</Typography>
          <Typography variant="body2" color="text.secondary">
            Registra una venta ya realizada en tienda o por teléfono. Queda pagada, con recojo en tienda; al
            registrarla puedes emitir la boleta, factura o nota de venta.
          </Typography>
        </div>
        <Button variant="outlined" component={RouterLink} to="/admin/pedidos" sx={{ flexShrink: 0 }}>
          Ver órdenes de venta
        </Button>
      </Stack>

      <Grid
        container
        spacing={3}
        component="form"
        id="express-sale-form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Autocomplete
                  options={productOptions}
                  value={null}
                  loading={searchingProducts}
                  getOptionLabel={(option) => (option ? `${option.name} (SKU ${option.sku})` : '')}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  // El filtro lo hace la API: un producto encontrado por su serie no tiene la serie
                  // en su etiqueta y el filtro local por defecto lo ocultaria.
                  filterOptions={(options) => options}
                  noOptionsText="Sin resultados"
                  onChange={(event, value) => addProduct(value, value && sellableSerialFor(value, matchedSerial))}
                  // Texto controlado: al agregar un producto se limpia, listo para el siguiente escaneo.
                  inputValue={productSearch}
                  onInputChange={(event, value, reason) => reason !== 'reset' && setProductSearch(value)}
                  onKeyDown={handleSearchEnter}
                  onHighlightChange={(event, option, reason) => {
                    // Solo cuenta lo resaltado con las flechas: el mouse puede quedar encima de una
                    // opcion mientras llega un escaneo, y ese Enter no debe elegirla.
                    highlightedOption.current = reason === 'keyboard' ? option : null;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      autoFocus
                      inputRef={searchInputRef}
                      label="Buscar producto"
                      placeholder="Escanea o escribe la serie, nombre o SKU y presiona Enter"
                      // Etiqueta siempre arriba, para que el texto de ayuda se lea sin enfocar el campo.
                      slotProps={{ ...params.slotProps, inputLabel: { ...params.slotProps?.inputLabel, shrink: true } }}
                      helperText={
                        matchedSerial
                          ? `Serie ${matchedSerial.serie}${matchedSerial.status === 'in_stock' ? '' : ' (ya vendida)'}`
                          : 'Primero busca la serie exacta; si no existe, busca por nombre, SKU o código de barras.'
                      }
                    />
                  )}
                />

                <Stack sx={{ alignItems: { sm: 'flex-start' } }} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Producto libre (no registrado)"
                    placeholder="Descripción"
                    value={customDescription}
                    onChange={(e) => setCustomDescription(e.target.value)}
                    onKeyDown={handleCustomKeyDown}
                    slotProps={{ htmlInput: { maxLength: 255 } }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label={`Precio c/u (${currency === 'USD' ? 'US$' : 'S/'})`}
                    value={customPrice}
                    onChange={(e) => setCustomPrice(e.target.value)}
                    onKeyDown={handleCustomKeyDown}
                    slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                    sx={{ minWidth: { sm: 160 } }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addCustomItem}
                    disabled={!canAddCustom}
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap', height: 40 }}
                  >
                    Agregar
                  </Button>
                </Stack>

                {items.length === 0 ? (
                  <Alert severity="info">Aún no agregaste productos.</Alert>
                ) : (
                  <List disablePadding>
                    {items.map((item) => (
                      <ListItem
                        key={itemKey(item)}
                        divider
                        disableGutters
                        secondaryAction={
                          <Stack sx={{ alignItems: 'center' }} direction="row" spacing={1}>
                            <QuantityStepper
                              value={item.quantity}
                              onChange={(quantity) => updateQuantity(itemKey(item), quantity)}
                            />
                            <IconButton size="small" onClick={() => removeItem(itemKey(item))}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        }
                      >
                        <ListItemText
                          primary={item.product ? item.product.name : `${item.description} (producto libre)`}
                          secondary={`${formatMoney(unitPrice(item), currency)} c/u · Subtotal: ${formatMoney(unitPrice(item) * item.quantity, currency)}${item.serials?.length ? ` · Serie: ${item.serials.join(', ')}` : ''}`}
                          sx={{ pr: 20 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ position: { md: 'sticky' }, top: { md: 88 } }}>
            <CardContent>
              <Stack spacing={2.5}>
                <Typography variant="h6">Resumen</Typography>

                <TextField
                  fullWidth
                  label="Nombre del cliente (opcional)"
                  placeholder="Ej. Juan Pérez"
                  helperText={errors.customer_name?.message || 'Se imprime en la nota de venta, sin DNI.'}
                  error={Boolean(errors.customer_name)}
                  slotProps={{ htmlInput: { maxLength: 255 } }}
                  {...register('customer_name')}
                />

                <Controller
                  name="currency"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} select fullWidth label="Moneda">
                      <MenuItem value="PEN">Soles (S/)</MenuItem>
                      <MenuItem value="USD">Dólares (US$)</MenuItem>
                    </TextField>
                  )}
                />

                <Controller
                  name="payment_method"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      label="Método de pago"
                      error={Boolean(errors.payment_method)}
                      helperText={errors.payment_method?.message}
                    >
                      {EXPRESS_PAYMENT_METHODS.map((method) => (
                        <MenuItem key={method} value={method}>
                          {paymentMethodLabel(method)}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />

                <Divider />

                <Stack sx={{ justifyContent: 'space-between' }} direction="row">
                  <Typography variant="body2" color="text.secondary">
                    {items.reduce((sum, item) => sum + item.quantity, 0)} unidad(es)
                  </Typography>
                  <Typography variant="h5">{formatMoney(total, currency)}</Typography>
                </Stack>

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  startIcon={<ShoppingCartOutlinedIcon />}
                  disabled={isSubmitting || items.length === 0}
                >
                  {isSubmitting ? 'Registrando...' : 'Registrar venta'}
                </Button>
                {items.length > 0 && (
                  <Button color="inherit" onClick={resetSale} disabled={isSubmitting}>
                    Vaciar venta
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <OrderDetailDialog
        open={Boolean(createdOrder)}
        onClose={handleDetailClose}
        order={createdOrder}
        statuses={statuses}
        onChanged={refreshCreatedOrder}
      />
    </Stack>
  );
}
